import React, { useState, useEffect } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import Zoom from '@mui/material/Zoom';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  checkedIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    boxShadow: theme.shadows[2],
    borderRadius: '50%',
    backgroundColor: 'currentColor',
  },
  circularProgress: {
    display: 'grid',
    color: theme.palette.icon.light,
  },
}));

interface QSwitchProps {
  checked: boolean;
  onChange: (e?: any, value?: any) => void;
  disabled?: boolean;
  showCheckProgress?: boolean;
  showUncheckProgress?: boolean;
  id?: string;
  className?: string;
}

const QSwitch = React.forwardRef((props: QSwitchProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
  const { showCheckProgress, showUncheckProgress, checked, onChange, disabled, id, ...otherProps } = props;
  const { classes } = useStyles();
  const [internalChecked, setInternalChecked] = useState(false);
  const [internalDisabled, setInternalDisabled] = useState(false);

  useEffect(() => {
    setInternalChecked(checked);
    setInternalDisabled(false);
  }, [checked]);

  useEffect(() => {
    if (!showCheckProgress && !showUncheckProgress) {
      setInternalDisabled(false);
    }
  }, [showCheckProgress, showUncheckProgress]);

  return (
    <Switch
      id={`${id}-${checked ? 'checked' : 'unchecked'}`}
      sharedcomponentid={'Q_SWITCH'}
      ref={ref}
      disabled={disabled || internalDisabled}
      checked={checked}
      onChange={(event, value) => {
        setInternalChecked(value);
        setInternalDisabled(true);
        if (onChange) {
          onChange(event, value);
        }
      }}
      color='primary'
      {...(showCheckProgress && internalChecked !== checked
        ? {
            icon: (
              <Zoom in mountOnEnter unmountOnExit>
                <span className={classes.checkedIcon}>
                  <CircularProgress className={classes.circularProgress} size={12} />
                </span>
              </Zoom>
            ),
          }
        : undefined)}
      {...(showUncheckProgress && internalChecked !== checked
        ? {
            checkedIcon: (
              <Zoom in mountOnEnter unmountOnExit>
                <span className={classes.checkedIcon}>
                  <CircularProgress className={classes.circularProgress} size={12} />
                </span>
              </Zoom>
            ),
          }
        : undefined)}
      {...otherProps}
    />
  );
});

QSwitch.displayName = 'QSwitch';

export default QSwitch;
